/**
 * Mapeo centralizado de colores para estados de órdenes y productos
 *
 * DECISION (unificación jul 2026): ordenes se alinea al sistema de badges
 * corporativo UNIFICADO (pastel + texto oscuro accesible + borde), usando la
 * paleta compartida src/components/ui/badgeColors.js. Se conservan los TONOS
 * históricos de ordenes (azul=sin preparar, rojo=en preparación, verde=preparado,
 * ámbar=en revisión) porque el sistema corporativo se derivó justamente de ellos.
 */

import { BADGE_COLORS, getBadgeStyles } from "../../../components/ui/badgeColors";
import { ORDER_STATE, ORDER_STATE_N2 } from "../../../config/constants";

// Estados de Nave 1. DECISION: el verde es para FINALIZADO, el único estado que cierra el
// documento. PREPARADO va en TEAL: ya está preparado pero aún le queda la revisión.
const ORDER_STATE_BADGE_COLORS = {
  [ORDER_STATE.SIN_PREPARAR]: BADGE_COLORS.BLUE,
  [ORDER_STATE.EN_PREPARACION]: BADGE_COLORS.RED,
  [ORDER_STATE.PREPARADO]: BADGE_COLORS.TEAL,
  [ORDER_STATE.EN_REVISION]: BADGE_COLORS.AMBER,
  [ORDER_STATE.FINALIZADO]: BADGE_COLORS.GREEN,
  "0": BADGE_COLORS.GRAY, // Indefinido
};

// Estados de Nave 2: mismo tono por estado que en Nave 1 ('3' es PREPARADO, en TEAL)
const ORDER_STATE_N2_BADGE_COLORS = {
  [ORDER_STATE_N2.SIN_PREPARAR]: BADGE_COLORS.BLUE,
  [ORDER_STATE_N2.EN_PREPARACION]: BADGE_COLORS.RED,
  [ORDER_STATE_N2.PREPARADO]: BADGE_COLORS.TEAL,
  "4": BADGE_COLORS.AMBER, // En revisión (solo registros antiguos)
  "0": BADGE_COLORS.GRAY,
};

// Estados de PRODUCTOS: 0=Pendiente, 1=Completado, 2=Falta
const PRODUCT_STATE_BADGE_COLORS = {
  "0": BADGE_COLORS.AMBER, // Pendiente
  "1": BADGE_COLORS.GREEN, // Completado
  "2": BADGE_COLORS.RED, // Falta
};

/**
 * Obtiene el objeto de color corporativo para un estado de orden/pedido
 * @param {string|number} state - Estado de Nave 1 (ORDER_STATE)
 * @returns {Object} Objeto de color de BADGE_COLORS
 */
export const getOrderStateBadgeColor = (state) => {
  return ORDER_STATE_BADGE_COLORS[String(state)] || BADGE_COLORS.GRAY;
};

/**
 * Obtiene el objeto de color corporativo para un estado de Nave 2
 * @param {string|number} state - Estado de registro_pedido_n2
 * @returns {Object} Objeto de color de BADGE_COLORS
 */
export const getOrderStateN2BadgeColor = (state) => {
  return ORDER_STATE_N2_BADGE_COLORS[String(state)] || BADGE_COLORS.GRAY;
};

/**
 * Obtiene el objeto de color corporativo para un estado de producto
 * @param {string|number} state - Estado del producto (0, 1, 2)
 * @returns {Object} Objeto de color de BADGE_COLORS
 */
export const getProductStateBadgeColor = (state) => {
  return PRODUCT_STATE_BADGE_COLORS[String(state)] || BADGE_COLORS.GRAY;
};

/**
 * Estilos sx (pastel corporativo) para un Chip con estado de orden/pedido
 * @param {string|number} state - Estado de la orden
 * @param {Object} options - Opciones (height, fontSize, withBorder)
 * @returns {Object} Objeto de estilos para sx prop
 */
export const getOrderStateBadgeStyles = (state, options = {}) => {
  return getBadgeStyles(getOrderStateBadgeColor(state), options);
};

/**
 * Estilos sx (pastel corporativo) para un Chip con estado de Nave 2
 * @param {string|number} state - Estado de registro_pedido_n2
 * @param {Object} options - Opciones (height, fontSize, withBorder)
 * @returns {Object} Objeto de estilos para sx prop
 */
export const getOrderStateN2BadgeStyles = (state, options = {}) => {
  return getBadgeStyles(getOrderStateN2BadgeColor(state), options);
};

/**
 * Estilos sx (pastel corporativo) para un Chip con estado de producto
 * @param {string|number} state - Estado del producto
 * @param {Object} options - Opciones (height, fontSize, withBorder)
 * @returns {Object} Objeto de estilos para sx prop
 */
export const getProductStateBadgeStyles = (state, options = {}) => {
  return getBadgeStyles(getProductStateBadgeColor(state), options);
};

/**
 * Color hexadecimal del tono (borde) de un estado de orden.
 * Útil para acentos no-badge (bordes de card, indicadores).
 * @param {string|number} state - Estado de la orden
 * @returns {string} Color hexadecimal
 */
export const getOrderStateColor = (state) => getOrderStateBadgeColor(state).border;

/**
 * Color hexadecimal del tono (borde) de un estado de producto.
 * @param {string|number} state - Estado del producto
 * @returns {string} Color hexadecimal
 */
export const getProductStateColor = (state) => getProductStateBadgeColor(state).border;
